import React from "react";
import { useTranslation } from "react-i18next";
import Select, { StylesConfig } from "react-select";
import styles from "./styles.module.scss";
import {
  ScheduleResourceResponse,
  ResourceCategoryResponse,
  compareByAdminOrder,
  compareBaysInAdminOrder,
} from "app/utils/request/scheduling/facilityResources";
import {
  ResourceSelection,
  categoryValue,
  resourceValue,
  splitOptionValues,
  allEntryLabel,
  categoryOfferable,
  coveredResourceCount,
  dropCoveredResources,
  resourceCountLabel,
} from "./selection";

type Option = { value: string; label: string };
type Group = { label: string; options: Option[] };

interface ResourceSelectorProps {
  resources: ScheduleResourceResponse[];
  categories: ResourceCategoryResponse[];
  value: ResourceSelection;
  onChange: (selection: ResourceSelection) => void;
  disabled?: boolean;
  // False inside the Resources block, which shows its own title.
  showLabel?: boolean;
}

// The select control of the booking option modals (Figma 145:57563,
// 145:55775). A pick shows as a count in the control ("2 Resources"), not as
// chips: the chips sit in a row under the control (see ResourceSelector
// below), so `controlShouldRenderValue` stays false on the `Select`. The hex
// values name the `booking-tokens` token they match; react-select styles are
// a JS object, so they cannot read the SCSS tokens directly.
const selectStyles: StylesConfig = {
  control: (base: any) => ({
    ...base,
    minHeight: "48px",
    borderRadius: "12px", // $cn-radius-control
    border: "1px solid #e7e7e7", // $cn-line
    backgroundColor: "#fff", // $cn-surface
    boxShadow: "none",
    padding: "0 16px",
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "15px 0",
  }),
  singleValue: (base: any) => ({ ...base, color: "#000" }), // $cn-black
  placeholder: (base: any) => ({ ...base, color: "#8c8c8c" }), // $cn-muted
  input: (base: any) => ({ ...base, margin: 0, padding: 0, color: "#000" }),
  indicatorSeparator: () => ({ display: "none" }),
  // The chevron points up while the list is open, as in the Edit Facility
  // modal.
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: "#8c8c8c", // $cn-muted
    padding: 0,
    transition: "transform 0.2s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : undefined,
  }),
  option: (base: any) => ({ ...base, color: "#000" }), // $cn-black
  menu: (base: any) => ({ ...base, zIndex: 20 }),
};

/**
 * Picks the entries a booking option names.
 *
 * The select lists each bookable section as one "All" entry, and the bays of
 * the section under it, in the order of the Resources page. All entries are alternatives: a booking takes one
 * bay, and the facility assigns the first free one. This version shows no
 * location, so the picker leaves out every section and resource of that kind.
 */
const ResourceSelector: React.FC<ResourceSelectorProps> = ({
  resources,
  categories,
  value,
  onChange,
  disabled = false,
  showLabel = true,
}) => {
  const { t } = useTranslation();

  const bookableCategories = categories
    .filter((category) => category.kind === "bookable")
    .sort(compareByAdminOrder);
  const bookableResources = resources.filter(
    (resource) => resource.kind === "bookable"
  );

  const entryCount =
    value.scheduleResourceIds.length + value.resourceCategoryIds.length;

  const categoryOption = (category: ResourceCategoryResponse): Option => ({
    value: categoryValue(category.id),
    label: allEntryLabel(category.title, t("All")),
  });

  const resourceOption = (resource: ScheduleResourceResponse): Option => ({
    value: resourceValue(resource.id),
    label: resource.active
      ? resource.title
      : `${resource.title} (${t("Inactive")})`,
  });

  // An inactive resource stays in the list while this option still names it, so
  // its label explains why the resource gets no booking.
  const selectable = (resource: ScheduleResourceResponse) =>
    value.scheduleResourceIds.includes(resource.id) || resource.active;

  const groups: Group[] = bookableCategories.map((category) => ({
    label: category.title,
    options: [
      ...(categoryOfferable(category, bookableResources, value.resourceCategoryIds)
        ? [categoryOption(category)]
        : []),
      // A section the option names whole hides its single bays, so the list
      // never offers a bay twice (CFTB-3551).
      ...bookableResources
        .filter(
          (resource) =>
            resource.resourceCategoryId === category.id &&
            !value.resourceCategoryIds.includes(category.id) &&
            selectable(resource)
        )
        .sort(compareByAdminOrder)
        .map(resourceOption),
    ],
  }));

  const selectedOptions: Option[] = [
    ...bookableCategories
      .filter((category) => value.resourceCategoryIds.includes(category.id))
      .map(categoryOption),
    ...bookableResources
      .filter((resource) => value.scheduleResourceIds.includes(resource.id))
      .sort(compareBaysInAdminOrder)
      .map(resourceOption),
  ];

  const noActiveResource = bookableResources.every(
    (resource) => !resource.active
  );

  const noOptionsMessage = () => {
    if (bookableCategories.length === 0) {
      return t("No active resource yet. Add one on the Resources page.");
    }

    return t("No more resources to add.");
  };

  const hint = () => {
    if (noActiveResource && entryCount === 0) {
      return t("No active resource yet. Add one on the Resources page.");
    }
    if (value.resourceCategoryIds.length > 0 || entryCount > 1) {
      return t(
        "A booking takes one of these resources. The facility assigns the first free one."
      );
    }

    return t(
      "A booking on this option takes the resource, so nothing else can book it at that time."
    );
  };

  // The control shows this count instead of the chips (Figma 145:57563). The
  // count is of the resources the entries cover, not of the entries: "All
  // Simulator Bays" with two active bays gives "2 Resources".
  const resourceCount = coveredResourceCount(value, bookableResources);
  const countLabel = resourceCountLabel(
    resourceCount,
    t("No resource"),
    t("{{count}} Resource", { count: resourceCount }),
    t("{{count}} Resources", { count: resourceCount })
  );

  const removeEntry = (optionValue: string) => {
    onChange(
      splitOptionValues(
        selectedOptions
          .filter((option) => option.value !== optionValue)
          .map((option) => option.value)
      )
    );
  };

  return (
    <div>
      {showLabel && (
        <label className={styles.label} htmlFor="facilityResource">
          {t("Resources")}
        </label>
      )}
      <Select
        inputId="facilityResource"
        aria-label={showLabel ? undefined : t("Resources")}
        isMulti
        isDisabled={disabled}
        isClearable={false}
        controlShouldRenderValue={false}
        styles={selectStyles}
        options={groups}
        value={selectedOptions}
        onChange={(options: any) =>
          onChange(
            dropCoveredResources(
              splitOptionValues(
                ((options as Option[]) || []).map((option) => option.value)
              ),
              bookableResources
            )
          )
        }
        formatGroupLabel={(group: Group) => (
          // A div, not a span: the booking dialogs colour a span red.
          <div className={styles.group_heading}>{group.label}</div>
        )}
        noOptionsMessage={noOptionsMessage}
        placeholder={countLabel}
        // The list opens below the control, as the other selects of the
        // modal do. Above the control, the list stays open after a pick
        // (closeMenuOnSelect) and covers the block header, so a click on
        // the header does not close the block.
        closeMenuOnSelect={false}
      />
      {/* The chip row under the control, not inside it (Figma 145:57563). */}
      {selectedOptions.length > 0 && (
        <div className={styles.chips}>
          {selectedOptions.map((option) => (
            <div className={styles.chip} key={option.value}>
              <span>{option.label}</span>
              <button
                type="button"
                className={styles.chip_remove}
                aria-label={t("Remove {{label}}", { label: option.label })}
                onClick={() => removeEntry(option.value)}
              >
                <i className="ico ico-x" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
      {/* A paragraph, not a span: the booking dialogs colour every span of a
          form row red, as an error. */}
      <p className={styles.hint}>{hint()}</p>
    </div>
  );
};

export default ResourceSelector;

/**
 * The resource part of a booking-option payload.
 *
 * Empty while the facility has the feature off, so a service without the
 * feature never receives the keys. With the feature on, an empty list clears
 * the entries of the option, so the caller must pass the flag and not the
 * length of the lists. The request layer drops every other empty list from a
 * body, so these keys are on the keep list in `app/utils/request/model`.
 */
export function resourceIdsPayload(
  resourcesEnabled: boolean,
  scheduleResourceIds: number[],
  resourceCategoryIds: number[]
): { schedule_resource_ids?: number[]; resource_category_ids?: number[] } {
  if (!resourcesEnabled) {
    return {};
  }

  return {
    schedule_resource_ids: scheduleResourceIds,
    resource_category_ids: resourceCategoryIds,
  };
}
